import React, {useCallback, useEffect, useState} from "react";
import {Box, Grid} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import {HorizontalBar} from "react-chartjs-2";
import PrescriptionService from "@user-service/PrescriptionService";

/**
 * 상세 페이지 - 기초율동 + 좌우연결 통합
 */
export default function DetailBasicRhythm({prescriptionID}) {
    const classes = useStyles();
    const [rhythmData, setRhythmData] = useState(null);
    const [connectivityData, setConnectivityData] = useState(null);

    const fetchRhythm = useCallback(async () => {
        try {
            const data = await PrescriptionService.getBasicRhythm(prescriptionID);
            if (data) {
                setRhythmData(data);
            }
        } catch (e) {}
    }, [prescriptionID]);

    const fetchConnectivity = useCallback(async () => {
        try {
            const data = await PrescriptionService.getConnectivity(prescriptionID);
            if (data) {
                setConnectivityData(data);
            }
        } catch (e) {}
    }, [prescriptionID]);

    useEffect(() => {
        fetchRhythm().then();
        fetchConnectivity().then();
    }, [fetchRhythm, fetchConnectivity]);

    const ch1 = rhythmData ? rhythmData.ch1 : null;
    const ch2 = rhythmData ? rhythmData.ch2 : null;
    const correlation = connectivityData ? connectivityData.correlation : 0;
    const coherence = connectivityData ? connectivityData.coherence : 0;

    return (
        <Box>
            {/* 기초율동 */}
            <Box mt={1}>
                <Grid container spacing={4}>
                    <Grid item xs={6}>
                        <RhythmDetail
                            label="좌뇌 (Ch1)"
                            color="rgba(0,178,245,1)"
                            bgColor="rgba(0,178,245,0.06)"
                            data={ch1}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <RhythmDetail
                            label="우뇌 (Ch2)"
                            color="rgba(199,116,210,1)"
                            bgColor="rgba(199,116,210,0.06)"
                            data={ch2}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* 좌우연결 */}
            <Box mt={6}>
                <Box>
                    <Grid container spacing={4}>
                        <Grid item xs={6}>
                            <Box className={classes.metricBox} style={{borderTop: '3px solid rgba(75,192,192,1)'}}>
                                <Typography className={classes.metricLabel}>
                                    상관성 (Correlation)
                                </Typography>
                                <Typography variant={'body2'} className={classes.metricDesc}>
                                    시간 영역에서 좌/우 뇌파의 파형 유사도를 측정합니다.
                                </Typography>
                                <Box mt={2}>
                                    <Typography className={classes.metricValue} style={{color: 'rgba(75,192,192,1)'}}>
                                        {correlation}
                                    </Typography>
                                </Box>
                                <Box mt={2} style={{height: 60, position: 'relative'}}>
                                    <HorizontalBar
                                        key={`corr-${correlation}`}
                                        data={makeBarData('상관성', correlation, 'rgba(75,192,192,0.6)', 'rgba(75,192,192,1)')}
                                        options={barOptions}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box className={classes.metricBox} style={{borderTop: '3px solid rgba(153,102,255,1)'}}>
                                <Typography className={classes.metricLabel}>
                                    일관성 (Coherence)
                                </Typography>
                                <Typography variant={'body2'} className={classes.metricDesc}>
                                    주파수 영역에서 좌/우 뇌파의 위상 동기화 정도를 측정합니다.
                                </Typography>
                                <Box mt={2}>
                                    <Typography className={classes.metricValue} style={{color: 'rgba(153,102,255,1)'}}>
                                        {coherence}
                                    </Typography>
                                </Box>
                                <Box mt={2} style={{height: 60, position: 'relative'}}>
                                    <HorizontalBar
                                        key={`coh-${coherence}`}
                                        data={makeBarData('일관성', coherence, 'rgba(153,102,255,0.6)', 'rgba(153,102,255,1)')}
                                        options={barOptions}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
}

function RhythmDetail({label, color, bgColor, data}) {
    const classes = useStyles();

    const peakHz = data ? data.peakHz : '-';
    const peakMv = data ? data.peakMv : '-';
    const relativePower = data ? (data.relativePower * 100).toFixed(1) : '-';

    return (
        <Box className={classes.detailBox} style={{borderTop: `3px solid ${color}`, backgroundColor: bgColor}}>
            <Typography className={classes.detailLabel} style={{color}}>
                {label}
            </Typography>
            <Box mt={3}>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <Box textAlign="center">
                            <Typography className={classes.valueLabel}>피크 주파수</Typography>
                            <Typography className={classes.valueNumber}>
                                {peakHz !== '-' ? `${peakHz} Hz` : '-'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box textAlign="center">
                            <Typography className={classes.valueLabel}>평균값</Typography>
                            <Typography className={classes.valueNumber}>
                                {peakMv !== '-' ? `${peakMv} μV` : '-'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box textAlign="center">
                            <Typography className={classes.valueLabel}>비중 (상대파워)</Typography>
                            <Typography className={classes.valueNumber}>
                                {relativePower !== '-' ? `${relativePower}%` : '-'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

function makeBarData(label, value, bg, border) {
    return {
        labels: [label],
        datasets: [{
            data: [value],
            backgroundColor: [bg],
            borderColor: [border],
            borderWidth: 1,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
        }]
    };
}

const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {display: false},
    tooltips: {
        backgroundColor: 'rgba(0,0,0,0.7)',
        callbacks: {
            label: function (tooltipItem) {
                return `${tooltipItem.value}`;
            }
        }
    },
    scales: {
        xAxes: [{
            display: true,
            gridLines: {display: true, color: 'rgba(0,0,0,0.05)'},
            ticks: {
                fontColor: '#666', fontSize: 11,
                min: 0, max: 100, stepSize: 25,
            },
        }],
        yAxes: [{
            display: false,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
        }]
    }
};

const useStyles = makeStyles(() => ({
    description: {
        color: '#777',
        marginBottom: 0,
    },
    detailBox: {
        borderRadius: 6,
        padding: '20px 24px',
    },
    detailLabel: {
        fontWeight: 'bold',
        fontSize: 16,
    },
    valueLabel: {
        fontSize: 12,
        color: '#999',
        marginBottom: 4,
    },
    valueNumber: {
        fontWeight: 'bold',
        fontSize: 22,
        color: '#333',
    },
    metricBox: {
        borderRadius: 6,
        padding: '20px 24px',
        backgroundColor: '#fafafa',
    },
    metricLabel: {
        fontWeight: 'bold',
        fontSize: 16,
        color: '#333',
    },
    metricDesc: {
        color: '#999',
        fontSize: 12,
        marginTop: 4,
    },
    metricValue: {
        fontWeight: 'bold',
        fontSize: 36,
        textAlign: 'center',
    },
}));
